import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
  const [activeStage, setActiveStage] = useState('define');

  const stages = {
    define: {
      title: 'Define: Envision Your Future State',
      description: 'Transform aspirations into measurable technology roadmaps with evidence-based planning.',
      sections: [
        {
          title: 'Strategic Foundation',
          items: [
            'Stakeholder interviews and alignment',
            'Current-state assessment',
            'Future State Operating Model (FSOM)',
            'Business outcome mapping'
          ]
        },
        {
          title: 'Value Planning',
          items: [
            'KPI target setting and measurement',
            'ROI analysis and value case',
            'Risk assessment and mitigation',
            'Technology roadmap development'
          ]
        }
      ],
      stats: ['100% Sponsor Alignment', '2-4 wk Discovery Phase']
    },
    design: {
      title: 'Design: Engineer Your Solution',
      description: 'Architecture and detailed design with security, scalability, and automation built in from the start.',
      sections: [
        {
          title: 'Technical Design',
          items: [
            'Solution architecture',
            'Integration planning',
            'Security design',
            'Data model design'
          ]
        },
        {
          title: 'Implementation Planning',
          items: [
            'Deployment strategy',
            'Testing approach',
            'Training plan',
            'Change management'
          ]
        }
      ],
      stats: ['DevSecOps Practices', 'Security Built-In']
    },
    deliver: {
      title: 'Deliver: Execute With Precision',
      description: 'Controlled implementation with testing, documentation, and training to ensure quality outcomes.',
      sections: [
        {
          title: 'Execution',
          items: [
            'Phased rollout',
            'Quality assurance',
            'User acceptance testing',
            'Documentation'
          ]
        },
        {
          title: 'Enablement',
          items: [
            'User training',
            'Knowledge transfer',
            'SOPs and runbooks',
            'Go-live support'
          ]
        }
      ],
      stats: ['Controlled Rollouts', 'Tested & Documented']
    },
    sustain: {
      title: 'Sustain: Optimize & Improve',
      description: 'Post-implementation monitoring, support, and continuous improvement to maximize long-term value.',
      sections: [
        {
          title: 'Ongoing Support',
          items: [
            'Performance monitoring',
            'Issue resolution',
            'System optimization',
            'Regular health checks'
          ]
        },
        {
          title: 'Continuous Improvement',
          items: [
            'KPI tracking',
            'User feedback',
            'Enhancement planning',
            'Technology updates'
          ]
        }
      ],
      stats: ['Proactive Monitoring', 'Continuous Optimization']
    }
  };

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="about-hero-container">
          <h1 className="about-hero-title">The 3DS Methodology</h1>
          <p className="about-hero-subtitle">Define. Design. Deliver. Sustain.</p>
          <p className="about-hero-description">
            A proven framework for predictable, secure technology transformation
          </p>
        </div>
      </section>

      <section className="methodology-stages">
        <div className="about-container">
          <div className="stages-container">
            <div className="stages-tabs">
              <button 
                className={`stage-tab ${activeStage === 'define' ? 'active' : ''}`}
                onClick={() => setActiveStage('define')}
              >
                Define
              </button>
              <button 
                className={`stage-tab ${activeStage === 'design' ? 'active' : ''}`}
                onClick={() => setActiveStage('design')}
              >
                Design
              </button>
              <button 
                className={`stage-tab ${activeStage === 'deliver' ? 'active' : ''}`}
                onClick={() => setActiveStage('deliver')}
              >
                Deliver
              </button>
              <button 
                className={`stage-tab ${activeStage === 'sustain' ? 'active' : ''}`}
                onClick={() => setActiveStage('sustain')}
              >
                Sustain
              </button>
            </div>

            <div className="stage-content">
              <h2 className="stage-title">{stages[activeStage].title}</h2>
              <p className="stage-description">{stages[activeStage].description}</p>
              
              <div className="stage-sections">
                {stages[activeStage].sections.map((section, idx) => (
                  <div key={idx} className="stage-section">
                    <h3 className="section-title">{section.title}</h3>
                    <ul className="section-items">
                      {section.items.map((item, itemIdx) => (
                        <li key={itemIdx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="stage-stats">
                {stages[activeStage].stats.map((stat, idx) => (
                  <div key={idx} className="stage-stat">{stat}</div>
                ))}
              </div>

              <div className="stage-actions">
                <Link to="/contact" className="btn-stage-primary">Start Your Journey</Link>
                <Link to="/contact" className="btn-stage-secondary">Learn More</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-why">
        <div className="about-container">
          <h2 className="about-section-title">Why Vertechnology?</h2>
          <p className="about-subtitle">Over 20 years of proven excellence in technology transformation</p>
          
          <div className="why-grid">
            <div className="why-card">
              <h3 className="why-title">Strategy First</h3>
              <p className="why-description">Every initiative anchored to measurable business outcomes and KPIs</p>
            </div>
            <div className="why-card">
              <h3 className="why-title">Engineering Excellence</h3>
              <p className="why-description">Modern practices with DevSecOps, automation, and quality at speed</p>
            </div>
            <div className="why-card">
              <h3 className="why-title">Security-by-Default</h3>
              <p className="why-description">Built-in security controls, continuous monitoring, and compliance</p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-cta">
        <div className="about-container">
          <h2 className="about-cta-title">Ready to Transform Your Technology?</h2>
          <p className="about-cta-description">
            Let's discuss how our 3DS methodology can deliver predictable, secure results for your business.
          </p>
          <Link to="/contact" className="btn-about-primary">Schedule a Discovery Workshop</Link>
        </div>
      </section>
    </div>
  );
};

export default About;
